import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Box,
  Group,
  Stack,
  Text,
  Badge,
  Button,
  Switch,
  SegmentedControl,
  TextInput,
  Card,
  ActionIcon,
  Tooltip,
  FileButton,
  LoadingOverlay,
  ThemeIcon,
  ScrollArea,
  Code,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import {
  IconSparkles,
  IconWindow,
  IconMaximize,
  IconFileCode,
  IconUpload,
  IconDownload,
  IconTrash,
  IconRefresh,
  IconDeviceFloppy,
  IconCheck,
  IconBook,
  IconFolderOpen,
} from '@tabler/icons-react';
import {
  BotProvider,
  WebBotProviderData,
  WebBotCustomUiMode,
  CustomUiFileItem,
  useGetApiBotProviderCustomUiGetConfig,
  useGetApiBotProviderCustomUiListFiles,
  usePutApiBotProviderCustomUiUpdateConfig,
  usePostApiBotProviderCustomUiUploadZip,
  useDeleteApiBotProviderCustomUiReset,
  usePostApiBotProviderCustomUiApplyBotConfig,
} from '@/API';
import { GetBotProviderCustomUiDownloadZipUrl } from '@/APIURL';
import { AppSplitter } from '@/Components/AppSplitter';
import { CustomUiDevGuideModal } from './CustomUiDevGuideModal';
import { CustomUiExplorerModal } from './CustomUiExplorerModal';
import { WebBotLivePreview } from '../WebBotLivePreview';

export interface WebBotCustomUiModalProps {
  workspaceId: string;
  botProviderId: string;
  botProvider: BotProvider;
  opened: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

export function WebBotCustomUiModal({
  workspaceId,
  botProviderId,
  botProvider,
  opened,
  onClose,
  onUpdated,
}: WebBotCustomUiModalProps) {
  const [guideOpened, { open: openGuide, close: closeGuide }] = useDisclosure(false);
  const [explorerOpened, { open: openExplorer, close: closeExplorer }] = useDisclosure(false);
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);

  // Config query (MongoDB)
  const configQuery = useGetApiBotProviderCustomUiGetConfig(
    { workspaceId, botProviderId },
    { query: { enabled: opened } as any }
  );

  // Files query (S3)
  const filesQuery = useGetApiBotProviderCustomUiListFiles(
    { workspaceId, botProviderId },
    { query: { enabled: opened } as any }
  );

  const customConfig = configQuery.data?.data;
  const files: CustomUiFileItem[] = filesQuery.data?.data || [];
  const regularFiles: CustomUiFileItem[] = useMemo(() => {
    return files.filter((f) => f.filePath && !f.filePath.endsWith('/'));
  }, [files]);
  const totalSize = useMemo(() => {
    return regularFiles.reduce((acc, f) => acc + (f.size || 0), 0);
  }, [regularFiles]);

  // Form states
  const [formEnabled, setFormEnabled] = useState(false);
  const [formMode, setFormMode] = useState<WebBotCustomUiMode>(WebBotCustomUiMode.Window);
  const [formEntryFile, setFormEntryFile] = useState('index.html');

  // Sync states when config loads
  useEffect(() => {
    if (customConfig) {
      setFormEnabled(!!customConfig.enabled);
      setFormMode(customConfig.mode === WebBotCustomUiMode.Headless ? WebBotCustomUiMode.Headless : WebBotCustomUiMode.Window);
      setFormEntryFile(customConfig.entryFile || 'index.html');
    }
  }, [customConfig]);

  // Mutations
  const updateConfigMutation = usePutApiBotProviderCustomUiUpdateConfig();
  const uploadZipMutation = usePostApiBotProviderCustomUiUploadZip();
  const resetMutation = useDeleteApiBotProviderCustomUiReset();
  const applyBotConfigMutation = usePostApiBotProviderCustomUiApplyBotConfig();

  // Save config
  const handleSaveConfig = async () => {
    try {
      await updateConfigMutation.mutateAsync({
        params: { workspaceId, botProviderId },
        data: {
          enabled: formEnabled,
          mode: formMode,
          entryFile: formEntryFile,
        },
      });
      notifications.show({ message: '自訂 UI 設定已儲存', color: 'green', icon: <IconCheck size={16} /> });
      configQuery.refetch();
      setPreviewRefreshKey((prev) => prev + 1);
      onUpdated?.();
    } catch {
      notifications.show({ message: '儲存自訂 UI 設定失敗', color: 'red' });
    }
  };

  // Upload ZIP (Clean Deploy)
  const handleUploadZip = async (file: File | null) => {
    if (!file) return;
    try {
      const res = await uploadZipMutation.mutateAsync({
        params: { workspaceId, botProviderId },
        data: { file },
      });
      const data = res.data;
      const recBotConfig = data.recommendedBotConfig;
      const rawConfig = data.rawBotConfig;

      notifications.show({
        message: data.configFound
          ? `自訂 UI ZIP 上傳成功！已讀取 cakechat.json 規格並自動啟用${data.themeName ? `（${data.themeName}）` : ''}`
          : '自訂 UI ZIP 上傳並發布成功（已自動啟用）',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      configQuery.refetch();
      filesQuery.refetch();
      setPreviewRefreshKey((prev) => prev + 1);
      onUpdated?.();

      if (recBotConfig && (rawConfig || Object.keys(recBotConfig).length > 0)) {
        // Collect badges for preview
        const badges: Array<{ label: string; color: string }> = [];
        const layout = recBotConfig.layout;
        if (layout?.windowWidth || layout?.windowHeight) {
          badges.push({
            label: `視窗尺寸: ${layout.windowWidth ?? '-'} × ${layout.windowHeight ?? '-'} px`,
            color: 'blue',
          });
        }
        if (recBotConfig.theme?.themeColor) {
          badges.push({ label: `主題顏色: ${recBotConfig.theme.themeColor}`, color: 'teal' });
        }
        if (recBotConfig.text?.title) {
          badges.push({ label: `標題: ${recBotConfig.text.title}`, color: 'indigo' });
        }
        if (recBotConfig.text?.botName) {
          badges.push({ label: `助理名稱: ${recBotConfig.text.botName}`, color: 'violet' });
        }
        if (recBotConfig.extensions && recBotConfig.extensions.length > 0) {
          badges.push({ label: `擴充插件: ${recBotConfig.extensions.length} 個`, color: 'cyan' });
        }
        if (recBotConfig.features) {
          badges.push({ label: '功能開關', color: 'grape' });
        }
        if (recBotConfig.userBubble || recBotConfig.botBubble) {
          badges.push({ label: '對話氣泡樣式', color: 'pink' });
        }
        if (recBotConfig.header || recBotConfig.footer) {
          badges.push({ label: '頁首/頁尾樣式', color: 'orange' });
        }
        if (recBotConfig.background) {
          badges.push({ label: '聊天背景樣式', color: 'lime' });
        }

        const configJsonString = JSON.stringify(rawConfig ?? recBotConfig, null, 2);

        modals.openConfirmModal({
          title: '套用 cakechat.json 推薦設定',
          children: (
            <Stack gap="xs">
              <Text size="sm">
                檢測到此主題包含推薦的機器人外觀與參數配置，是否立即將這些設定覆蓋合併至目前的機器人？
              </Text>
              {badges.length > 0 && (
                <Group gap={6} mt={4} wrap="wrap">
                  {badges.map((b, idx) => (
                    <Badge key={idx} size="sm" color={b.color} variant="light">
                      {b.label}
                    </Badge>
                  ))}
                </Group>
              )}
              <ScrollArea.Autosize mah={180} mt="xs">
                <Code block style={{ fontSize: '11px', lineHeight: 1.4 }}>
                  {configJsonString}
                </Code>
              </ScrollArea.Autosize>
            </Stack>
          ),
          labels: { confirm: '確認套用推薦設定', cancel: '維持現有設定' },
          confirmProps: { color: 'blue' },
          onConfirm: async () => {
            try {
              await applyBotConfigMutation.mutateAsync({
                params: { workspaceId, botProviderId },
                data: { botConfig: (rawConfig ?? recBotConfig) as any },
              });
              notifications.show({
                message: '已成功套用 cakechat.json 推薦設定',
                color: 'green',
                icon: <IconCheck size={16} />,
              });
              setPreviewRefreshKey((prev) => prev + 1);
              onUpdated?.();
            } catch {
              notifications.show({
                message: '套用推薦設定失敗',
                color: 'red',
              });
            }
          },
        });
      }
    } catch {
      notifications.show({ message: '自訂 UI 上傳失敗，請確認檔案格式', color: 'red' });
    }
  };

  // Reset / Clear
  const handleReset = () => {
    modals.openConfirmModal({
      title: '清空自訂 UI',
      children: (
        <Text size="sm">
          確定要清空所有自訂 UI 檔案並停用自訂 UI 嗎？此動作將刪除 S3 上該機器人專屬的所有自訂 UI 檔案，無法復原。
        </Text>
      ),
      labels: { confirm: '確認清空', cancel: '取消' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await resetMutation.mutateAsync({
            params: { workspaceId, botProviderId },
          });
          notifications.show({ message: '已清空自訂 UI 檔案並停用', color: 'blue' });
          configQuery.refetch();
          filesQuery.refetch();
          setPreviewRefreshKey((prev) => prev + 1);
          onUpdated?.();
        } catch {
          notifications.show({ message: '清空自訂 UI 失敗', color: 'red' });
        }
      },
    });
  };

  // Format bytes helper
  const formatBytes = (bytes?: number) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
  };

  const livePreviewData = useMemo<Partial<WebBotProviderData>>(() => {
    const rawData = (botProvider.providerData as WebBotProviderData) || {};
    return {
      ...rawData,
      customUi: {
        ...rawData.customUi,
        enabled: formEnabled,
        mode: formMode,
        entryFile: formEntryFile,
      },
    };
  }, [botProvider.providerData, formEnabled, formMode, formEntryFile]);

  // Left Column Content
  const leftContent = (
    <Stack gap="md" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
      {/* Configuration Card */}
      <Card withBorder radius="md" p="md" style={{ flexShrink: 0 }}>
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Text fw={600} size="sm">
                啟用自訂 UI (Custom UI)
              </Text>
              {formEnabled ? (
                <Badge color="green" size="sm" variant="light">
                  已啟用
                </Badge>
              ) : (
                <Badge color="gray" size="sm" variant="outline">
                  已停用
                </Badge>
              )}
            </Group>
            <Group gap="sm">
              <Switch
                checked={formEnabled}
                onChange={(e) => setFormEnabled(e.currentTarget.checked)}
                size="sm"
              />
              <Button
                size="xs"
                variant="filled"
                color="blue"
                leftSection={<IconDeviceFloppy size={14} />}
                onClick={handleSaveConfig}
                loading={updateConfigMutation.isPending}
              >
                儲存設定
              </Button>
            </Group>
          </Group>

          {formEnabled && (
            <>
              <div>
                <Text size="xs" fw={500} mb={4} c="dimmed">
                  運作模式 (Mode)
                </Text>
                <SegmentedControl
                  value={formMode}
                  onChange={(val) => setFormMode(val as WebBotCustomUiMode)}
                  data={[
                    {
                      value: WebBotCustomUiMode.Window,
                      label: (
                        <Group gap={6} justify="center">
                          <IconWindow size={14} />
                          <Text size="xs">視窗內部 (Window)</Text>
                        </Group>
                      ),
                    },
                    {
                      value: WebBotCustomUiMode.Headless,
                      label: (
                        <Group gap={6} justify="center">
                          <IconMaximize size={14} />
                          <Text size="xs">無頭嵌入 (Headless)</Text>
                        </Group>
                      ),
                    },
                  ]}
                  fullWidth
                  size="xs"
                />
              </div>

              <TextInput
                label="進入點檔案"
                description="自訂網頁根目錄入口，預設為 index.html"
                placeholder="index.html"
                value={formEntryFile}
                onChange={(e) => setFormEntryFile(e.currentTarget.value)}
                leftSection={<IconFileCode size={14} />}
                size="xs"
              />
            </>
          )}
        </Stack>
      </Card>

      {/* Deployment & Explorer Card */}
      <Card withBorder radius="md" p="md" style={{ flexShrink: 0 }}>
        <LoadingOverlay visible={uploadZipMutation.isPending} />
        <Stack gap="sm">
          <Group justify="space-between" align="center">
            <Group gap="xs">
              <Text fw={600} size="sm">
                檔案庫與部署
              </Text>
              <Badge size="xs" variant="light" color="blue">
                {regularFiles.length} 個檔案
              </Badge>
              <Badge size="xs" variant="outline" c="dimmed">
                {formatBytes(totalSize)}
              </Badge>
            </Group>
          </Group>

          <Group gap="xs" wrap="wrap">
            <Button
              size="xs"
              variant="filled"
              color="blue"
              leftSection={<IconFolderOpen size={14} />}
              onClick={openExplorer}
            >
              開啟檔案總管
            </Button>
            <FileButton onChange={handleUploadZip} accept=".zip">
              {(props) => (
                <Button
                  {...props}
                  size="xs"
                  variant="default"
                  leftSection={<IconUpload size={14} />}
                >
                  上傳 ZIP 發布
                </Button>
              )}
            </FileButton>
            {files.length > 0 && (
              <>
                <Button
                  component="a"
                  href={GetBotProviderCustomUiDownloadZipUrl(workspaceId, botProviderId)}
                  download
                  target="_blank"
                  size="xs"
                  variant="default"
                  leftSection={<IconDownload size={14} />}
                >
                  下載 ZIP
                </Button>
                <Tooltip label="清空所有自訂 UI 檔案">
                  <ActionIcon variant="light" color="red" size="sm" onClick={handleReset}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Tooltip>
              </>
            )}
          </Group>
        </Stack>
      </Card>
    </Stack>
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="sm" align="center">
            <ThemeIcon color="violet" variant="light" size="md">
              <IconSparkles size={18} />
            </ThemeIcon>
            <div>
              <Group gap="xs">
                <Text fw={600} size="md">
                  自訂 UI 工作台 (Custom UI Workbench)
                </Text>
                {formEnabled && (
                  <Badge color="green" size="xs" variant="light">
                    生效中
                  </Badge>
                )}
              </Group>
              <Text size="xs" c="dimmed">
                上傳專屬打造的 HTML/SPA 聊天介面，並在此進行微調與即時預覽。
              </Text>
            </div>
          </Group>
        }
        size="96vw"
        styles={{
          body: {
            height: 'calc(94vh - 80px)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            padding: '12px 16px',
          },
          content: {
            height: '92vh',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Stack gap="xs" style={{ flex: 1, minHeight: 0 }}>
          {/* Top Subheader */}
          <Group justify="space-between" align="center">
            <Text size="xs" c="dimmed">
              💡 自訂 UI 託管路徑：<code>/api/WebBot/custom/{botProviderId}/{formEntryFile}</code>
            </Text>
            <Group gap="xs">
              <Button
                variant="light"
                color="violet"
                size="xs"
                leftSection={<IconBook size={14} />}
                onClick={openGuide}
              >
                開發指南與 SDK 規格
              </Button>
              <Tooltip label="重新整理預覽畫面">
                <ActionIcon
                  variant="light"
                  color="blue"
                  size="sm"
                  onClick={() => setPreviewRefreshKey((prev) => prev + 1)}
                >
                  <IconRefresh size={14} />
                </ActionIcon>
              </Tooltip>
            </Group>
          </Group>

          {/* Main Dual-Column Splitter */}
          <Box style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <AppSplitter
              style={{
                height: '100%',
                minHeight: 0,
                flex: 1,
              }}
              styles={{
                root: {
                  height: '100%',
                  minHeight: 0,
                  flex: 1,
                },
                pane: {
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                  overflow: 'hidden',
                },
              }}
            >
              <AppSplitter.Pane defaultSize={52} min="420px" style={{ paddingRight: 8 }}>
                {leftContent}
              </AppSplitter.Pane>
              <AppSplitter.Pane defaultSize={48} min="380px" style={{ paddingLeft: 8 }}>
                <WebBotLivePreview
                  key={previewRefreshKey}
                  data={livePreviewData}
                  botProviderId={botProviderId}
                  workspaceId={workspaceId}
                />
              </AppSplitter.Pane>
            </AppSplitter>
          </Box>
        </Stack>
      </Modal>

      {/* Guide Modal */}
      <CustomUiDevGuideModal opened={guideOpened} onClose={closeGuide} />

      {/* Custom UI File Explorer Modal */}
      <CustomUiExplorerModal
        opened={explorerOpened}
        onClose={() => {
          closeExplorer();
          filesQuery.refetch();
          setPreviewRefreshKey((prev) => prev + 1);
          onUpdated?.();
        }}
        workspaceId={workspaceId}
        botProviderId={botProviderId}
        files={files}
        onFilesUpdated={() => {
          filesQuery.refetch();
          setPreviewRefreshKey((prev) => prev + 1);
          onUpdated?.();
        }}
      />
    </>
  );
}

export interface WebBotCustomUiButtonProps {
  workspaceId: string;
  botProviderId: string;
  botProvider: BotProvider;
  children: React.ReactNode;
  onUpdated?: () => void;
}

export function WebBotCustomUiButton({
  workspaceId,
  botProviderId,
  botProvider,
  children,
  onUpdated,
}: WebBotCustomUiButtonProps) {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <>
      <span onClick={open} style={{ display: 'inline-flex' }}>
        {children}
      </span>
      <WebBotCustomUiModal
        workspaceId={workspaceId}
        botProviderId={botProviderId}
        botProvider={botProvider}
        opened={opened}
        onClose={close}
        onUpdated={onUpdated}
      />
    </>
  );
}
